import React, { useEffect, useRef, useState } from 'react';
import { ExclamationIcon } from '@heroicons/react/solid';
import Banner from './Banner';
import Button from './Button';

/**
 * Shared error-state block for failed data fetches across the client portal.
 *
 * @param {object} props
 * @param {string} [props.message] - Error copy. Defaults to a generic message.
 * @param {() => void | Promise<unknown>} [props.onRetry] - When provided, renders a Retry button.
 * @param {boolean} [props.isRetrying] - Whether the retry is pending outside this component.
 * @param {string} [props.className] - Merged onto the outer wrapper.
 */
const ErrorState = ({ message, onRetry, isRetrying = false, className = '' }) => {
    const [internalRetrying, setInternalRetrying] = useState(false);
    const retryingRef = useRef(false);
    const mountedRef = useRef(true);
    const displayMessage = message || 'Something went wrong. Please try again.';
    const retryLabel = `Retry loading: ${displayMessage}`;
    const retryPending = isRetrying || internalRetrying;

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const finishRetry = () => {
        retryingRef.current = false;
        if (mountedRef.current) setInternalRetrying(false);
    };

    const handleRetry = () => {
        if (!onRetry || isRetrying || retryingRef.current) return;

        retryingRef.current = true;
        setInternalRetrying(true);

        try {
            const retryResult = onRetry();
            if (retryResult && typeof retryResult.then === 'function') {
                Promise.resolve(retryResult).then(finishRetry, finishRetry);
            } else {
                finishRetry();
            }
        } catch (error) {
            finishRetry();
            throw error;
        }
    };

    return (
        <Banner
            variant="danger"
            icon={ExclamationIcon}
            title={displayMessage}
            aria-live="assertive"
            aria-atomic="true"
            className={className}
            action={onRetry && (
                <Button
                    type="button"
                    onClick={handleRetry}
                    disabled={retryPending}
                    aria-busy={retryPending}
                    aria-label={retryLabel}
                    variant="danger"
                    size="sm"
                >
                    {retryPending ? 'Retrying…' : 'Retry'}
                </Button>
            )}
        />
    );
};

export default ErrorState;
